import * as React from 'react';
import { Autocomplete, Checkbox, FormGroup, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { useQuery } from '@tanstack/react-query';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  ContainerType,
  ContainerTypeQueryKeys,
  fetchContainerTypes,
} from 'services/clients/containerType';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const ContainerTypeFilter: React.FC = () => {
  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS, true);
  const containerTypeQuery = useQuery(
    [ContainerTypeQueryKeys.ContainerTypes],
    ({ signal }) => fetchContainerTypes({ signal }),
    { staleTime: Infinity, cacheTime: Infinity }
  );
  const handleOnChange = (events: React.SyntheticEvent, values: ContainerType[]) => {
    setSearchParams({
      containerType: values.map((ct) => ct.key),
    });
  };

  const defaultValue = (containerTypeQuery.data || []).filter((ct) =>
    searchParams.containerType.includes(ct.key)
  );

  return (
    <FormGroup className="px-2">
      <Autocomplete
        multiple
        disablePortal
        fullWidth
        noOptionsText="No container type"
        disableCloseOnSelect
        options={containerTypeQuery.data || []}
        isOptionEqualToValue={(option, value) => {
          return option.key === value.key;
        }}
        getOptionLabel={(option) => option.name}
        defaultValue={defaultValue}
        loading={containerTypeQuery.isLoading}
        onChange={handleOnChange}
        renderOption={(optionProps, option, { selected }) => (
          <li {...optionProps} key={option.key}>
            <Checkbox icon={icon} size="small" checkedIcon={checkedIcon} checked={selected} />
            <p className="font-medium text-sm">{option.name}</p>
          </li>
        )}
        ChipProps={{
          deleteIcon: <CloseIcon style={{ fontSize: 14, margin: 0 }} />,
        }}
        renderInput={(params) => (
          <TextField {...params} size="small" variant="outlined" placeholder="Container Type" />
        )}
      />
    </FormGroup>
  );
};
ContainerTypeFilter.displayName = 'TrackAndTrace.Controls.filters.ContainerTypeFilter';
